import React, { useContext, useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  Loader2,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import AppContext from "../../context/AppContext";
import LangComp from "../../components/_app/langComp";
import translations from "../../components/common/Translations";
import { toast } from "../../utils/toast";

const OTP_LENGTH = 6;
const RESEND_DELAY_SECONDS = 30;

const languageData = [
  { id: 1, lang: "en", langText: "English" },
  { id: 2, lang: "ur", langText: "Urdu" },
  { id: 3, lang: "ar", langText: "Arabic" },
];

const defaultLabels = {
  title: "Verify your identity",
  description: "We sent a 6-digit code via SMS to",
  didntGetCode: "Didn't get the code?",
  verify: "Verify and continue",
  verifying: "Verifying...",
  resend: "Resend code",
  resendIn: "Resend in",
  sent: "A new verification code was sent.",
  invalid: "Please enter the complete verification code.",
  back: "Back to login",
  secureTitle: "Secure access to Blufield",
  secureDescription:
    "Your verification code helps keep your field operations protected.",
  digitLabel: "Verification digit",
  registeredNumber: "your registered number",
};

const maskPhoneNumber = (phoneNumber) => {
  const value = phoneNumber.trim();
  const match = value.match(/^(\+\d{1,3})\s?(\d)(?:[\d\s-]*)(\d{3})$/);

  return match ? `${match[1]} ${match[2]}\u2022\u2022\u2022\u2022 ${match[3]}` : value;
};

const LoginOtpScreen = ({
  phoneNumber = "",
  onVerify,
  onResend,
  onBack,
  isVerifying = false,
  isResending = false,
  labels = {},
}) => {
  const { theme, langMode, toggleLangMode } = useContext(AppContext);
  const inputRefs = useRef([]);
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const [digits, setDigits] = useState(() => Array(OTP_LENGTH).fill(""));
  const [secondsRemaining, setSecondsRemaining] = useState(
    RESEND_DELAY_SECONDS,
  );
  const activeLanguage = String(langMode || "en").toLowerCase().slice(0, 2);
  const languageCopy = translations[activeLanguage] || translations.en || {};
  const copy = {
    ...defaultLabels,
    title: languageCopy.loginOtpTitle || defaultLabels.title,
    description: languageCopy.loginOtpDescription || defaultLabels.description,
    didntGetCode:
      languageCopy.loginOtpDidntGetCode || defaultLabels.didntGetCode,
    verify: languageCopy.loginOtpVerify || defaultLabels.verify,
    verifying: languageCopy.loginOtpVerifying || defaultLabels.verifying,
    resend: languageCopy.loginOtpResend || defaultLabels.resend,
    resendIn: languageCopy.loginOtpResendIn || defaultLabels.resendIn,
    sent: languageCopy.loginOtpResendSuccess || defaultLabels.sent,
    invalid: languageCopy.loginOtpIncomplete || defaultLabels.invalid,
    back: languageCopy.loginOtpBack || defaultLabels.back,
    secureTitle: languageCopy.loginOtpSecureTitle || defaultLabels.secureTitle,
    secureDescription:
      languageCopy.loginOtpSecureDescription || defaultLabels.secureDescription,
    digitLabel: languageCopy.loginOtpDigitLabel || defaultLabels.digitLabel,
    registeredNumber:
      languageCopy.loginOtpRegisteredNumber || defaultLabels.registeredNumber,
    ...labels,
  };
  const isDark = theme === "dark";
  const isHighContrast = theme === "high-contrast";
  const isRtl = activeLanguage === "ar" || activeLanguage === "ur";
  const maskedPhone = phoneNumber
    ? maskPhoneNumber(phoneNumber)
    : copy.registeredNumber;
  const descriptionParts = copy.description.split("{phone}");
  const otp = digits.join("");

  useEffect(() => {
    if (secondsRemaining <= 0) return undefined;

    const timer = window.setInterval(() => {
      setSecondsRemaining((current) => Math.max(current - 1, 0));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [secondsRemaining]);

  const handleChange = (index, value) => {
    const nextValue = value.replace(/\D/g, "").slice(-1);
    const nextDigits = [...digits];
    nextDigits[index] = nextValue;
    setDigits(nextDigits);

    if (nextValue && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pastedDigits = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    const nextDigits = Array.from({ length: OTP_LENGTH }, (_, index) =>
      pastedDigits[index] || "",
    );
    setDigits(nextDigits);
    inputRefs.current[Math.min(pastedDigits.length, OTP_LENGTH) - 1]?.focus();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (otp.length !== OTP_LENGTH) {
      toast.error(copy.invalid);
      inputRefs.current[otp.length]?.focus();
      return;
    }

    onVerify?.(otp);
  };

  const handleResend = async () => {
    if (secondsRemaining > 0 || isResending) return;

    setDigits(Array(OTP_LENGTH).fill(""));
    inputRefs.current[0]?.focus();
    setSecondsRemaining(RESEND_DELAY_SECONDS);
    try {
      await onResend?.();
    } catch {
      setSecondsRemaining(0);
    }
  };

  const pageBackground = isDark
    ? "bg-slate-950 text-white"
    : isHighContrast
      ? "bg-white text-black"
      : "bg-white text-slate-900";
  const mutedText = isDark ? "text-slate-300" : "text-slate-500";
  const inputClassName = isHighContrast
    ? "border-2 border-black bg-white text-black focus:border-black focus:ring-2 focus:ring-black/20"
    : isDark
      ? "border-slate-600 bg-slate-800 text-white focus:border-[#5da4df] focus:ring-2 focus:ring-[#236DB4]/40"
      : "border-slate-200 bg-white text-slate-900 focus:border-[#236DB4] focus:ring-2 focus:ring-[#236DB4]/20";

  return (
    <main
      dir={isRtl ? "rtl" : "ltr"}
      className={`min-h-[100dvh] overflow-x-hidden ${pageBackground}`}
    >
      <div className="flex min-h-[100dvh] flex-col lg:flex-row">
        <section className="flex w-full flex-1 flex-col lg:w-[55%]">
          <div className="flex min-h-[100dvh] w-full flex-col px-4 py-6 sm:px-8 md:px-10 lg:px-12 xl:px-16">
            <div className="flex items-center justify-between">
              <img
                src="https://res.cloudinary.com/dpdvpazfv/image/upload/v1753344904/Bluefield_x0jmp9.png"
                alt="Blufield"
                className={`h-8 object-contain ${isDark ? "brightness-0 invert" : ""}`}
              />
              <LangComp
                isShowIcon
                variant={isDark ? "dark" : "light"}
                languageDataList={languageData}
                toggleLangMode={toggleLangMode}
                isNavbarOpen={isNavbarOpen}
                menuPosition="absolute"
              />
            </div>

            <div className="flex flex-1 items-center justify-center py-10">
              <div className="w-full max-w-md text-center">
                <div
                  className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full ${
                    isHighContrast
                      ? "border-2 border-black bg-white"
                      : isDark
                        ? "bg-[#236DB4]/20 text-[#8dc5f2]"
                        : "bg-[#236DB4]/10 text-[#236DB4]"
                  }`}
                >
                  <ShieldCheck className="h-8 w-8" aria-hidden="true" />
                </div>

                <h1 className="text-2xl font-bold sm:text-3xl">{copy.title}</h1>
                <p className={`mx-auto mt-3 max-w-sm text-sm leading-6 ${mutedText}`}>
                  {descriptionParts.map((part, index) => (
                    <React.Fragment key={`${part}-${index}`}>
                      {part}
                      {index < descriptionParts.length - 1 && (
                        <strong className={isDark ? "text-white" : "text-slate-800"}>
                          {maskedPhone}
                        </strong>
                      )}
                    </React.Fragment>
                  ))}
                </p>

                <form className="mt-8" onSubmit={handleSubmit}>
                  <div
                    className="flex w-full justify-center gap-2 sm:gap-3"
                    onPaste={handlePaste}
                    aria-label={copy.title}
                  >
                    {digits.map((digit, index) => (
                      <input
                        key={index}
                        ref={(element) => {
                          inputRefs.current[index] = element;
                        }}
                        value={digit}
                        onChange={(event) => handleChange(index, event.target.value)}
                        onKeyDown={(event) => handleKeyDown(event, index)}
                        inputMode="numeric"
                        autoComplete={index === 0 ? "one-time-code" : "off"}
                        maxLength={1}
                        aria-label={`${copy.digitLabel} ${index + 1}`}
                        dir="ltr"
                        style={{ textAlign: "center", direction: "ltr" }}
                        className={`h-12 w-10 rounded-xl border text-center text-xl font-bold outline-none transition sm:h-14 sm:w-12 ${inputClassName}`}
                      />
                    ))}
                    
                  </div>

                  <button data-button-intent="primary"
                    type="submit"
                    disabled={isVerifying}
                    className={`mt-7 flex min-h-[46px] w-full items-center justify-center rounded-xl px-4 py-3 text-base font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-70 ${
                      isHighContrast
                        ? "border-2 border-black bg-black hover:bg-neutral-800"
                        : "bg-[#1F5C8F] hover:bg-[#1a4d78]"
                    }`}
                  >
                    {isVerifying ? (
                      <Loader2 className="h-5 w-5 animate-spin" aria-label={copy.verifying} />
                    ) : (
                      copy.verify
                    )}
                  </button>

                  <div className="mt-5 flex min-h-10 items-center justify-between gap-3 text-sm">
                    <span
                      className={`min-w-0 ${isRtl ? "text-right" : "text-left"} ${mutedText}`}
                    >
                      {copy.didntGetCode}
                    </span>
                    <div className="flex shrink-0 items-center gap-2">
                      {secondsRemaining > 0 && (
                        <>
                          <span className={mutedText}>{copy.resendIn}</span>
                          <span className="font-semibold text-[#236DB4]">
                            {String(Math.floor(secondsRemaining / 60)).padStart(2, "0")}:
                            {String(secondsRemaining % 60).padStart(2, "0")}
                          </span>
                        </>
                      )}
                      {secondsRemaining <= 0 && (
                        <button data-button-intent="primary"
                          type="button"
                          onClick={handleResend}
                          disabled={isResending}
                          className="inline-flex min-h-10 items-center gap-1 rounded-lg px-2 font-semibold text-[#236DB4] hover:bg-[#236DB4]/10 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <RefreshCw className={`h-4 w-4 ${isResending ? "animate-spin" : ""}`} />
                          {copy.resend}
                        </button>
                      )}
                    </div>
                  </div>
                </form>

                <button
                  type="button"
                  onClick={onBack}
                  className={`mt-6 inline-flex min-h-10 items-center gap-2 rounded-lg px-3 text-sm font-medium ${
                    isDark ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <ArrowLeft
                    className={`h-4 w-4 ${isRtl ? "rotate-180" : ""}`}
                    aria-hidden="true"
                  />
                  {copy.back}
                </button>
              </div>
            </div>
          </div>
        </section>

        <aside className="relative hidden min-h-[100dvh] overflow-hidden bg-[#236DB4] lg:flex lg:w-[45%] lg:items-center lg:justify-center">
          <img
            src="/assets/slide1.jpg"
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B2E63]/70 to-[#0B2E63]/85" />
          <div className="relative z-10 max-w-lg px-8 text-center text-white xl:px-12">
            <ShieldCheck className="mx-auto mb-5 h-12 w-12" aria-hidden="true" />
            <h2 className="text-3xl font-semibold xl:text-4xl">{copy.secureTitle}</h2>
            <p className="mt-4 text-base leading-7 text-white/85 xl:text-lg">
              {copy.secureDescription}
            </p>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default LoginOtpScreen;